import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { FileImage, Paperclip, X } from 'lucide-react'
import { Alert, Button, IconButton, Input, Modal, Textarea } from '@/components/ui'
import { useSupportEmail } from '@/features/authorization'
import { useT } from '@/internationalization/i18n'
import { toast } from '@/stores'
import { useSendSupportRequest } from './api'
import {
  FILE_VARS,
  fileMessage,
  isUnconfirmed,
  serverMessage,
  showsEmailFallback,
  type T,
} from './errors'
import {
  ACCEPT_ATTRIBUTE,
  SUPPORT_LIMITS,
  addFiles,
  formatBytes,
  removeFile,
  validateDescription,
  validateSubject,
  type RejectedFile,
  type TextProblem,
} from './validation'

function subjectMessage(t: T, problem: TextProblem | null): string | undefined {
  if (problem === 'required') return t('support.error.subject.required')
  if (problem === 'too_long') return t('support.error.subject.tooLong', { max: SUPPORT_LIMITS.subjectMax })
  return undefined
}

function descriptionMessage(t: T, problem: TextProblem | null): string | undefined {
  if (problem === 'required') return t('support.error.description.required')
  if (problem === 'too_long') {
    return t('support.error.description.tooLong', { max: SUPPORT_LIMITS.descriptionMax })
  }
  return undefined
}

/** "Or email us at <address>." with the address as a link; the sentence is the translation's. */
function EmailFallback({ t, address }: { t: T; address: string }) {
  const [before, after] = t('support.error.emailFallback', { address }).split(address)
  return (
    <p className="mt-1">
      {before}
      <a href={`mailto:${address}`} className="underline">
        {address}
      </a>
      {after}
    </p>
  )
}

export interface SupportModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * The Contact Support form. Replaces a `mailto:` link, which opened a blank
 * draft in whatever mail client the person had (or none) and could carry no
 * screenshot.
 *
 * Nothing here chooses who receives the request or who it is from -- the form
 * sends a subject, a description and files, and the server supplies the rest.
 *
 * A failure keeps everything the person typed and attached, so retrying is one
 * click. Success is shown only after the server has said the message was
 * accepted by the mail provider.
 */
export function SupportModal({ isOpen, onClose }: SupportModalProps) {
  const t = useT()
  const send = useSendSupportRequest()
  const supportEmail = useSupportEmail()
  const fileInput = useRef<HTMLInputElement>(null)
  // A ref as well as `isPending`: two clicks in the same tick both see
  // `isPending === false`, because state only updates on the next render.
  const inFlight = useRef(false)

  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [rejected, setRejected] = useState<RejectedFile[]>([])
  const [submitted, setSubmitted] = useState(false)
  const [failure, setFailure] = useState<{
    message: string
    unconfirmed: boolean
    emailFallback: boolean
  } | null>(null)

  const pending = send.isPending

  // A closed dialog holds no draft. Nothing is kept between openings.
  useEffect(() => {
    if (isOpen) return
    setSubject('')
    setDescription('')
    setFiles([])
    setRejected([])
    setSubmitted(false)
    setFailure(null)
    send.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const subjectProblem = submitted ? validateSubject(subject) : null
  const descriptionProblem = submitted ? validateDescription(description) : null

  const close = () => {
    if (!pending) onClose()
  }

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? [])
    // Cleared so choosing the same file again, after removing it, fires change.
    event.target.value = ''
    if (picked.length === 0) return
    const next = addFiles(files, picked)
    setFiles(next.files)
    setRejected(next.rejected)
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (inFlight.current || pending) return
    setSubmitted(true)
    setFailure(null)
    if (validateSubject(subject) || validateDescription(description)) return

    inFlight.current = true
    send.mutate(
      { subject, description, attachments: files },
      {
        onSuccess: () => {
          toast.success(t('support.success.title'), t('support.success.body'))
          onClose()
        },
        onError: (error) =>
          setFailure({
            message: serverMessage(t, error),
            unconfirmed: isUnconfirmed(error),
            // Only when the deployment has named an address: no address, no line.
            emailFallback: showsEmailFallback(error, supportEmail),
          }),
        onSettled: () => {
          inFlight.current = false
        },
      },
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('support.title')}
      description={t('support.intro')}
      closeOnOverlayClick={!pending}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="support-form" variant="primary" isLoading={pending}>
            {pending ? t('support.sending') : t('support.submit')}
          </Button>
        </>
      }
    >
      <form id="support-form" onSubmit={onSubmit} noValidate className="space-y-4">
        {failure && (
          <div role="alert">
            <Alert
              tone="danger"
              title={t(failure.unconfirmed ? 'support.error.titleUnconfirmed' : 'support.error.title')}
            >
              {failure.message}
              {failure.emailFallback && supportEmail && <EmailFallback t={t} address={supportEmail} />}
            </Alert>
          </div>
        )}

        <Input
          label={t('support.subject.label')}
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={t('support.subject.placeholder')}
          error={subjectMessage(t, subjectProblem)}
          disabled={pending}
          autoComplete="off"
          // A hard stop a little past the limit: the counted length is after
          // whitespace collapse, so this only catches runaway pastes.
          maxLength={SUPPORT_LIMITS.subjectMax * 4}
        />

        <Textarea
          label={t('support.issue.label')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('support.issue.placeholder')}
          error={descriptionMessage(t, descriptionProblem)}
          disabled={pending}
          rows={6}
        />

        <div>
          <p className="mb-1.5 text-sm font-medium text-fg">{t('support.attach.label')}</p>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPT_ATTRIBUTE}
            onChange={onPick}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
          />
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Paperclip aria-hidden className="h-4 w-4" />}
            onClick={() => fileInput.current?.click()}
            disabled={pending || files.length >= SUPPORT_LIMITS.maxFiles}
          >
            {t('support.attach.button')}
          </Button>
          <p className="mt-1.5 text-sm text-fg-subtle">
            {t('support.attach.hint', { count: SUPPORT_LIMITS.maxFiles, file: FILE_VARS.max, total: FILE_VARS.total })}
          </p>

          {files.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5"
                >
                  <FileImage aria-hidden className="h-4 w-4 shrink-0 text-fg-subtle" />
                  <span className="min-w-0 flex-1 truncate text-sm text-fg">{file.name}</span>
                  <span className="shrink-0 text-sm text-fg-subtle">{formatBytes(file.size)}</span>
                  <IconButton
                    label={t('support.attach.remove', { name: file.name })}
                    icon={<X />}
                    size="sm"
                    disabled={pending}
                    onClick={() => setFiles((current) => removeFile(current, index))}
                  />
                </li>
              ))}
            </ul>
          )}

          {rejected.length > 0 && (
            <ul role="alert" className="mt-3 space-y-1 text-sm text-danger">
              {rejected.map((item, index) => (
                <li key={`${item.name}-${index}`}>
                  <span className="font-medium">{item.name}</span> — {fileMessage(t, item.problem)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </form>
    </Modal>
  )
}
